import React, { useRef } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ExploreIcon from '@mui/icons-material/Explore';
import SecurityIcon from '@mui/icons-material/Security';
import ExtensionIcon from '@mui/icons-material/Extension';
import StorageIcon from '@mui/icons-material/Storage';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ShieldIcon from '@mui/icons-material/Shield';
import BugReportIcon from '@mui/icons-material/BugReport';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import WebIcon from '@mui/icons-material/Web';
import TuneIcon from '@mui/icons-material/Tune';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimelineIcon from '@mui/icons-material/Timeline';
import LinkIcon from '@mui/icons-material/Link';
import GavelIcon from '@mui/icons-material/Gavel';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import HttpIcon from '@mui/icons-material/Http';

// Simple feature badge component
const FeatureBadge: React.FC<{ label: string; variant?: 'primary' | 'warning' | 'secondary' }> = ({
  label,
  variant = 'primary'
}) => (
  <Chip
    label={label}
    size="small"
    color={variant}
    variant="outlined"
    sx={{
      fontSize: '0.65rem',
      height: 18,
      ml: 1,
      '& .MuiChip-label': { px: 0.8 }
    }}
  />
);

// Category definitions
interface CategoryDef {
  id: string;
  label: string;
  description: string;
  color: string;
  borderColor: string;
  bgColor: string;
}

const categories: CategoryDef[] = [
  { id: 'authentication', label: 'Authentication', description: 'Test OAuth flows, decode tokens, inspect caches, and trace MSAL events.', color: '#312e81', borderColor: '#4c1d95', bgColor: '#e0e7ff' },
  { id: 'helpers', label: 'Helpers & Debugging', description: 'Diagnostic utilities for OIDC metadata, MSAL errors, claims extensions, and endpoint lookups.', color: '#115e59', borderColor: '#0f766e', bgColor: '#ccfbf1' },
  { id: 'workshop', label: 'OAuth Workshop & Learning', description: 'Interactive guides, visualizers, and reference material for mastering OAuth 2.0 and Microsoft identity.', color: '#92400e', borderColor: '#b45309', bgColor: '#fef3c7' },
  { id: 'graph', label: 'Graph & API Exploration', description: 'Query Microsoft Graph, build OData requests, and manage OAuth2 consent grants.', color: '#312e81', borderColor: '#4c1d95', bgColor: '#e0e7ff' },
  { id: 'security', label: 'Security', description: 'Analyze application security posture, attack surface, and credential hygiene.', color: '#7f1d1d', borderColor: '#991b1b', bgColor: '#fee2e2' },
  { id: 'management', label: 'Application Management', description: 'Edit manifests, manage claims mapping policies, and configure application settings.', color: '#312e81', borderColor: '#4c1d95', bgColor: '#e0e7ff' },
];

const ToolsTab: React.FC = () => {
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollToCategory = (id: string) => {
    categoryRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box>
      {/* Introduction Box */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'primary.lighter', borderLeft: '5px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BuildIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
            Tools & Utilities
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          AppConfig² provides a comprehensive collection of <strong>specialized tools</strong> designed to enhance your Microsoft Entra ID application management experience.
          These utilities help you test, analyze, debug, and optimize your application configurations with precision and efficiency.
        </Typography>
        <Typography variant="body2" color="text.primary">
          Each tool is purpose-built to address specific aspects of application lifecycle management, from authentication testing to comprehensive security analysis and claims extensions.
          Note that some tools have different access levels depending on your edition.
        </Typography>
      </Paper>

      {/* Quick-Nav Chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
        {categories.map(cat => (
          <Chip
            key={cat.id}
            label={cat.label}
            onClick={() => scrollToCategory(cat.id)}
            sx={{
              fontWeight: 600,
              cursor: 'pointer',
              bgcolor: cat.bgColor,
              color: cat.color,
              border: `1px solid ${cat.borderColor}`,
              '&:hover': { bgcolor: cat.borderColor, color: '#fff' },
            }}
          />
        ))}
      </Box>

      {/* ══════ CATEGORY 1 — Authentication (8 tools) ══════ */}
      <Box ref={(el: HTMLDivElement | null) => { categoryRefs.current['authentication'] = el; }} sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 1.5, py: 1, borderRadius: 1, bgcolor: '#e0e7ff', borderLeft: '4px solid #4c1d95' }}>
          <VerifiedUserIcon sx={{ mr: 1, color: '#312e81' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#312e81', flex: 1 }}>Authentication</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 1 }}>
          Test OAuth flows, decode tokens, inspect caches, and trace MSAL events.
        </Typography>

        <Grid container spacing={3}>
          {/* Auth Flow Tester */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <VerifiedUserIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Auth Flow Tester</Typography>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Test various authentication flows for your applications. This tool helps you:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Test OAuth 2.0 authorization code flow" /></ListItem>
                  <ListItem><ListItemText primary="• Test OAuth 2.0 implicit flow" /></ListItem>
                  <ListItem><ListItemText primary="• Test client credentials flow" /></ListItem>
                  <ListItem><ListItemText primary="• Test hybrid flow" /></ListItem>
                  <ListItem><ListItemText primary="• Validate OIDC configurations" /></ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Authorization Code" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Implicit Flow" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Client Credentials" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                  <Chip size="small" label="Hybrid Flow" sx={{ bgcolor: '#fce4ec', color: '#c2185b' }} />
                  <Chip size="small" label="OIDC" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Token & Consent Tester */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CenterFocusStrongIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Token & Consent Tester</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Request delegated tokens for Graph or custom APIs with explicit scopes or resource/.default.
                  Diagnose consent issues, test admin consent flows, analyze missing scopes and app roles; test endpoints with acquired tokens.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Tenant authority dropdown; switch user via popup; optional clientId override" /></ListItem>
                  <ListItem><ListItemText primary="• Requested vs granted delta; consent status helper; app-role assignments" /></ListItem>
                  <ListItem><ListItemText primary="• One-click Graph test presets (GET) with hint scopes" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Scopes delta" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Consent insights" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="App roles" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Raw OAuth Tester */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LockOpenIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Raw OAuth Tester</Typography>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Low-level AAD v2.0 authorize URL builder for interactive testing of implicit sign-in and token parsing.
                  Useful to quickly validate redirect URIs, state/nonce handling, and raw id/access token contents without SDK abstractions.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Build and launch raw /authorize requests (implicit: token id_token)" /></ListItem>
                  <ListItem><ListItemText primary="• Choose tenant authority (common/organizations/current tenant)" /></ListItem>
                  <ListItem><ListItemText primary="• See a parsed view of the last authorization request" /></ListItem>
                  <ListItem><ListItemText primary="• Decode returned tokens in-place; surface OAuth errors from callback" /></ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Implicit only" sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }} />
                  <Chip size="small" label="No SDK" sx={{ bgcolor: '#e0f2f1', color: '#00695c' }} />
                  <Chip size="small" label="Tenant switch" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Confidential Client Auth Debugger */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LockIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Confidential Client Auth Debugger</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Test and debug OAuth 2.0 Authorization Code Flow for web applications (confidential clients) with full token inspection.
                  No configuration changes needed - works with your existing web app setup.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Test web apps without modifying app registration (no SPA platform needed)" /></ListItem>
                  <ListItem><ListItemText primary="• Full Authorization Code Flow with optional PKCE support" /></ListItem>
                  <ListItem><ListItemText primary="• Server-side token exchange using /api/exchange-code endpoint" /></ListItem>
                  <ListItem><ListItemText primary="• Inspect ID tokens and access tokens in detail" /></ListItem>
                  <ListItem><ListItemText primary="• Test custom APIs and Microsoft Graph authentication flows" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Zero config" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Auth Code Flow" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Token inspection" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                  <Chip size="small" label="Custom APIs" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* JWT Token Decoder */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <VpnKeyIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">JWT Token Decoder</Typography>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Decode and analyze JWT tokens to understand their contents. This tool helps you:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• View token headers, payloads, and signatures" /></ListItem>
                  <ListItem><ListItemText primary="• Analyze claims and their values" /></ListItem>
                  <ListItem><ListItemText primary="• Verify token validity and expiration" /></ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Headers" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Claims" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Expiry (exp/nbf)" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Token Comparison Tool */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CompareArrowsIcon sx={{ color: '#7c3aed', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Token Comparison Tool</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Compare two JWT tokens side-by-side to spot differences in claims, scopes, and structure.
                  Ideal for comparing ID vs Access tokens, v1.0 vs v2.0 tokens, or tokens from different tenants.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Side-by-side claim diff with added/removed/changed highlighting" /></ListItem>
                  <ListItem><ListItemText primary="• Compare ID token vs access token claim sets" /></ListItem>
                  <ListItem><ListItemText primary="• Spot v1.0 vs v2.0 endpoint differences at a glance" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Side-by-side" sx={{ bgcolor: '#f3e5f5', color: '#7c3aed' }} />
                  <Chip size="small" label="Claim diff" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="v1 vs v2" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* MSAL Trace Viewer */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TimelineIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">MSAL Trace Viewer</Typography>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Real-time diagnostic tool that captures and displays all MSAL browser events,
                  providing deep visibility into authentication flows, token operations, and cache interactions.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Live event streaming grouped by correlation ID for end-to-end flow tracking" /></ListItem>
                  <ListItem><ListItemText primary="• Filter by event level (error/warning/info/verbose), event name, or message content" /></ListItem>
                  <ListItem><ListItemText primary="• Troubleshoot silent token acquisition, cache hits/misses, and refresh flows" /></ListItem>
                  <ListItem><ListItemText primary="• Export event logs for offline analysis or support tickets" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Real-time events" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Correlation tracking" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Event filtering" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                  <Chip size="small" label="Export logs" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* MSAL Cache Inspector */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StorageIcon sx={{ color: '#0288d1', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">MSAL Cache Inspector</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Inspect browser storage to understand MSAL token caching behavior.
                  View cached access tokens, refresh tokens, ID tokens, and account entries; simulate acquireTokenSilent to verify cache hits.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Browse sessionStorage / localStorage MSAL cache entries" /></ListItem>
                  <ListItem><ListItemText primary="• Decode cached tokens and inspect expiration, scopes, and audience" /></ListItem>
                  <ListItem><ListItemText primary="• Simulate silent token acquisition to test cache behavior" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Cache entries" sx={{ bgcolor: '#e1f5fe', color: '#0288d1' }} />
                  <Chip size="small" label="Token decode" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Silent simulation" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Auth comparison guidance */}
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Choosing the right authentication tool</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Auth Flow Tester vs. Raw OAuth Tester</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><VerifiedUserIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Auth Flow Tester" secondary="Guided experience powered by libraries for common OAuth/OIDC flows. Best for validating app setup with SDK-friendly defaults." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><LockOpenIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Raw OAuth Tester" secondary="Bare-metal authorize URL builder focused on the implicit flow and token parsing. Best for debugging redirect URIs and raw payloads." />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Confidential Client Auth Debugger vs. Adding SPA Platform</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><LockIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Confidential Client Auth Debugger (Recommended)" secondary="Test web applications with actual Authorization Code Flow without any configuration changes. Zero cleanup required." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><WebIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Add SPA Platform (Alternative)" secondary="Temporarily add SPA platform to web apps for testing via App Tester. Requires configuration changes and cleanup afterward." />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Token & Consent Tester vs. Test Authentication (AppTester)</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CenterFocusStrongIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Token & Consent Tester" secondary="Tests with your current app for rapid iteration on scopes, authorities, and consent flows during development." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><LockOpenIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Test Authentication (AppTester)" secondary="Tests as a different app registration. Best for verifying token claims after modifying app roles, optional claims, or claims mapping policies." />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* ══════ CATEGORY 2 — Helpers & Debugging (4 tools) ══════ */}
      <Box ref={(el: HTMLDivElement | null) => { categoryRefs.current['helpers'] = el; }} sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 1.5, py: 1, borderRadius: 1, bgcolor: '#ccfbf1', borderLeft: '4px solid #0f766e' }}>
          <BugReportIcon sx={{ mr: 1, color: '#115e59' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#115e59', flex: 1 }}>Helpers & Debugging</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 1 }}>
          Diagnostic utilities for OIDC metadata, MSAL errors, claims extensions, and endpoint lookups.
        </Typography>

        <Grid container spacing={3}>
          {/* MSAL Error Decoder */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BugReportIcon sx={{ color: '#d32f2f', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">MSAL Error Decoder</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Decode and troubleshoot MSAL authentication error codes. Paste any MSAL error code or message to get a human-readable
                  explanation, root cause analysis, and recommended remediation steps.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Look up MSAL error codes with detailed explanations" /></ListItem>
                  <ListItem><ListItemText primary="• Common error patterns with step-by-step resolution guides" /></ListItem>
                  <ListItem><ListItemText primary="• Links to relevant Microsoft documentation for each error" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Error lookup" sx={{ bgcolor: '#ffebee', color: '#d32f2f' }} />
                  <Chip size="small" label="Root cause" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                  <Chip size="small" label="Fix guidance" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* OIDC Metadata Inspector */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SecurityIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">OIDC Metadata Inspector</Typography>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Inspect OpenID Connect discovery documents, JWKS endpoints, and validate JWT signatures from any authority.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Fetch OIDC discovery metadata from any authority (Azure AD, B2C, custom IdP)" /></ListItem>
                  <ListItem><ListItemText primary="• View JWKS (JSON Web Key Sets) and public signing keys" /></ListItem>
                  <ListItem><ListItemText primary="• Match JWT kid/alg against JWKS entries and verify RS256 signatures" /></ListItem>
                  <ListItem><ListItemText primary="• Tenant dropdown for common/organizations/consumers/custom authorities" /></ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="OIDC Discovery" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="JWKS" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Signature Verify" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Claims Extensions Reference */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ExtensionIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Claims Extensions Reference</Typography>
                    <FeatureBadge label="Read-only" variant="secondary" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Read-only reference for directory and claims extensions in Entra ID. This tool helps you:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Browse directory extensions defined on applications, users, and groups" /></ListItem>
                  <ListItem><ListItemText primary="• Discover existing extension attributes and their data types across your tenant" /></ListItem>
                  <ListItem><ListItemText primary="• Explore Entra Connect synchronized extension attributes" /></ListItem>
                  <ListItem><ListItemText primary="• View extension attribute values applied to directory objects" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Read-only" sx={{ bgcolor: '#f5f5f5', color: '#616161' }} />
                  <Chip size="small" label="Directory Extensions" sx={{ bgcolor: '#e8eaf6', color: '#3949ab' }} />
                  <Chip size="small" label="Extension Attributes" sx={{ bgcolor: '#e0f2f1', color: '#00796b' }} />
                  <Chip size="small" label="Entra Connect" sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Entra ID Endpoints Explorer */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LinkIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Entra ID Endpoints Explorer</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Comprehensive, searchable reference directory for Entra ID, Microsoft Graph, Azure, and related service endpoints
                  across multiple cloud environments (Commercial, Government, China).
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Browse 200+ categorized endpoints (Authentication, Graph API, Azure services, B2C, etc.)" /></ListItem>
                  <ListItem><ListItemText primary="• Multi-cloud support: Azure Commercial, US Government (GCC/GCC High/DoD), China (21Vianet)" /></ListItem>
                  <ListItem><ListItemText primary="• Search by name, URL, or description; filter by category and cloud environment" /></ListItem>
                  <ListItem><ListItemText primary="• Quick copy-to-clipboard for endpoint URLs and tenant-specific formatting" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="200+ endpoints" sx={{ bgcolor: '#e8eaf6', color: '#3949ab' }} />
                  <Chip size="small" label="Multi-cloud" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Searchable" sx={{ bgcolor: '#e0f2f1', color: '#00695c' }} />
                  <Chip size="small" label="Copy URLs" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Helpers comparison guidance */}
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>OIDC Metadata Inspector vs. Token Decoder</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="OIDC Metadata Inspector" secondary="Start from an issuer/authority to validate discovery endpoints, examine supported algorithms, fetch JWKS, compare tenants, and troubleshoot key rotation & configuration drift." />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><VpnKeyIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Token Decoder" secondary="Start from a JWT for rapid claim decoding, semantic classification, quick JWKS fetch from its iss claim, and signature verification without first loading discovery manually." />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Claims Extensions Reference details (consolidated) */}
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Claims Extensions Reference — Details</Typography>
              <FeatureBadge label="Read-only" variant="secondary" />
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ExtensionIcon sx={{ mr: 1, color: '#3f51b5' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Directory Extensions</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Browse directory extensions defined on Entra ID objects. Each extension is owned by an application and supports String, Integer, Boolean, DateTime, and Binary types.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <StorageIcon sx={{ mr: 1, color: '#00796b' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Extension Attributes</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  View the values of directory extensions applied to objects. Find objects with specific extension attribute values and see usage across your tenant.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SyncAltIcon sx={{ mr: 1, color: '#ff8f00' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Entra Connect Extensions</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Discover on-premises Active Directory attributes synchronized via Entra Connect in the special extension_* format.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* ══════ CATEGORY 3 — OAuth Workshop & Learning (6 tools) ══════ */}
      <Box ref={(el: HTMLDivElement | null) => { categoryRefs.current['workshop'] = el; }} sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 1.5, py: 1, borderRadius: 1, bgcolor: '#fef3c7', borderLeft: '4px solid #b45309' }}>
          <AccountTreeIcon sx={{ mr: 1, color: '#92400e' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e', flex: 1 }}>OAuth Workshop & Learning</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 1 }}>
          Interactive guides, visualizers, and reference material for mastering OAuth 2.0 and Microsoft identity.
        </Typography>

        <Grid container spacing={3}>
          {/* OAuth Flow Decision Helper */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountTreeIcon sx={{ color: '#7b1fa2', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">OAuth Flow Decision Helper</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Interactive questionnaire that guides you to the right OAuth 2.0 / OpenID Connect flow for your application.
                  Answer a few questions about your app type, client capabilities, and security requirements to get a recommendation.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Step-by-step decision tree for flow selection" /></ListItem>
                  <ListItem><ListItemText primary="• Covers Auth Code + PKCE, Client Credentials, Device Code, OBO, and more" /></ListItem>
                  <ListItem><ListItemText primary="• Explains why each flow is recommended for the given scenario" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Interactive" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                  <Chip size="small" label="Decision tree" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Best practices" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* OAuth Flow Visualizer */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AltRouteIcon sx={{ color: '#0f766e', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">OAuth Flow Visualizer</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Interactive ladder (sequence) diagrams showing the step-by-step message exchange for common OAuth 2.0 flows.
                  Walk through each request and response to understand exactly what happens under the hood.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Animated sequence diagrams for Auth Code + PKCE, Implicit, and Refresh Token flows" /></ListItem>
                  <ListItem><ListItemText primary="• Step-by-step walkthrough with request/response details at each stage" /></ListItem>
                  <ListItem><ListItemText primary="• Highlights security-relevant parameters (code_verifier, state, nonce)" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Sequence diagram" sx={{ bgcolor: '#ccfbf1', color: '#0f766e' }} />
                  <Chip size="small" label="Step-by-step" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Interactive" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* App Registration vs Service Principal */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CompareArrowsIcon sx={{ color: '#0891b2', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">App Registration vs Service Principal</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Interactive guide that explains the relationship between Application objects (app registrations) and
                  Service Principals (enterprise applications) with concrete examples and troubleshooting scenarios.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Side-by-side comparison of properties and lifecycle" /></ListItem>
                  <ListItem><ListItemText primary="• Common troubleshooting scenarios (missing SP, consent issues, multi-tenant)" /></ListItem>
                  <ListItem><ListItemText primary="• Live lookup of your own app registrations and their service principals" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Conceptual" sx={{ bgcolor: '#cffafe', color: '#0891b2' }} />
                  <Chip size="small" label="Troubleshooting" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                  <Chip size="small" label="Live lookup" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* OAuth Endpoints & Flows Reference */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HttpIcon sx={{ color: '#0369a1', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">OAuth Endpoints & Flows Reference</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Comprehensive reference guide covering OAuth 2.0 endpoints, flows, PKCE, tokens, scopes, and troubleshooting (RFC 6749).
                  A one-stop reference for the Microsoft identity platform endpoint landscape.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Detailed endpoint documentation (/authorize, /token, /devicecode, etc.)" /></ListItem>
                  <ListItem><ListItemText primary="• PKCE explanation with code_verifier / code_challenge examples" /></ListItem>
                  <ListItem><ListItemText primary="• Token types, lifetimes, and scope conventions" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="RFC 6749" sx={{ bgcolor: '#dbeafe', color: '#0369a1' }} />
                  <Chip size="small" label="Endpoints" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="PKCE" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Microsoft Graph Reference Guide */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ExploreIcon sx={{ color: '#0ea5e9', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Microsoft Graph Reference Guide</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Practical Microsoft Graph API guide with endpoint patterns, permission models, common queries, batch requests, and troubleshooting tips.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Endpoint structure, versioning (v1.0 / beta), and OData query patterns" /></ListItem>
                  <ListItem><ListItemText primary="• Permission model: delegated vs application, least privilege guidance" /></ListItem>
                  <ListItem><ListItemText primary="• Common recipes: user profile, groups, applications, mail, calendar" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Graph API" sx={{ bgcolor: '#e0f2fe', color: '#0ea5e9' }} />
                  <Chip size="small" label="Permissions" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Recipes" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Permissions & Consent Reference */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <GavelIcon sx={{ color: '#7c4dff', mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Permissions & Consent Reference</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Comprehensive guide to permission types, consent mechanisms, token claims, and consent flows in the Microsoft identity platform.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Delegated vs Application permissions explained with examples" /></ListItem>
                  <ListItem><ListItemText primary="• Admin consent, user consent, and incremental consent workflows" /></ListItem>
                  <ListItem><ListItemText primary="• How permissions map to token claims (scp, roles, wids)" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Permissions" sx={{ bgcolor: '#ede7f6', color: '#7c4dff' }} />
                  <Chip size="small" label="Consent flows" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Token claims" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* ══════ CATEGORY 4 — Graph & API Exploration (3 tools) ══════ */}
      <Box ref={(el: HTMLDivElement | null) => { categoryRefs.current['graph'] = el; }} sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 1.5, py: 1, borderRadius: 1, bgcolor: '#e0e7ff', borderLeft: '4px solid #4c1d95' }}>
          <ExploreIcon sx={{ mr: 1, color: '#312e81' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#312e81', flex: 1 }}>Graph & API Exploration</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 1 }}>
          Query Microsoft Graph, build OData requests, and manage OAuth2 consent grants.
        </Typography>

        <Grid container spacing={3}>
          {/* Graph Explorer */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ExploreIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Graph Explorer</Typography>
                    <FeatureBadge label="Read-only in AppTesting" variant="warning" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Test and explore Microsoft Graph API endpoints. In AppTesting, this provides read-only access (Graph Viewer mode).
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Make Graph API requests directly from the UI" /></ListItem>
                  <ListItem><ListItemText primary="• View response data in a formatted display" /></ListItem>
                  <ListItem><ListItemText primary="• Test different Graph API operations (GET only in AppTesting)" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="GET (AppTesting)" sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }} />
                  <Chip size="small" label="Response viewer" sx={{ bgcolor: '#e0f2f1', color: '#00796b' }} />
                  <Chip size="small" label="Headers/Body" sx={{ bgcolor: '#e8eaf6', color: '#3949ab' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* OData Query Builder */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TuneIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">OData Query Builder</Typography>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Compose Microsoft Graph OData queries visually. Focuses on crafting $select, $filter, $orderby, $top, and $count for common resources
                  and executing them with paging support.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Field suggestions for applications, servicePrincipals, users, and groups" /></ListItem>
                  <ListItem><ListItemText primary="• Visual filters with contains/startswith/endswith and comparison operators" /></ListItem>
                  <ListItem><ListItemText primary="• Fetch-all paging with @odata.nextLink, optional $count (ConsistencyLevel)" /></ListItem>
                  <ListItem><ListItemText primary="• Toggle v1.0/beta; copy URL; download JSON results" /></ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="$select/$filter" sx={{ bgcolor: '#e8eaf6', color: '#3949ab' }} />
                  <Chip size="small" label="Paging" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                  <Chip size="small" label="v1.0 / beta" sx={{ bgcolor: '#e0f2f1', color: '#00695c' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* OAuth2 Consent Manager */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <GavelIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">OAuth2 Consent Manager</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Manage OAuth2 permission grants (consent records) across your tenant. View, analyze, and revoke delegated permissions
                  that users have granted to applications, helping maintain security hygiene and compliance.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• List all OAuth2PermissionGrants for applications and service principals" /></ListItem>
                  <ListItem><ListItemText primary="• View grant details: client app, resource, granted scopes, principal (user/admin)" /></ListItem>
                  <ListItem><ListItemText primary="• Identify admin consent (AllPrincipals) vs. user-specific consent grants" /></ListItem>
                  <ListItem><ListItemText primary="• Revoke unwanted or excessive consent grants to reduce attack surface" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="View grants" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Revoke consent" sx={{ bgcolor: '#ffebee', color: '#c62828' }} />
                  <Chip size="small" label="Filter/Search" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Audit trail" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Graph comparison guidance */}
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Graph Explorer vs. OData Query Builder</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><ExploreIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Graph Explorer" secondary="Full request runner for Microsoft Graph. Paste any endpoint, set method/body/headers, and execute. Great for trying APIs end-to-end and consenting missing scopes on the fly." />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><TuneIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="OData Query Builder" secondary="Purpose-built for crafting OData parameters over common Graph collections, with paging and download. Ideal when you need precise query shapes and large result navigation." />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>OAuth2 Consent Manager vs. Token & Consent Tester</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="OAuth2 Consent Manager" secondary="Tenant-wide view and management of consent records. View all existing consents, identify which apps/users have granted what, and revoke grants. Best for auditing and compliance." />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CenterFocusStrongIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Token & Consent Tester" secondary="Request new tokens with specific scopes for testing consent flows during development. Shows requested vs granted scopes delta. Best for active development and troubleshooting." />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* ══════ CATEGORY 5 — Security (3 tools) ══════ */}
      <Box ref={(el: HTMLDivElement | null) => { categoryRefs.current['security'] = el; }} sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 1.5, py: 1, borderRadius: 1, bgcolor: '#fee2e2', borderLeft: '4px solid #991b1b' }}>
          <ShieldIcon sx={{ mr: 1, color: '#7f1d1d' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#7f1d1d', flex: 1 }}>Security</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 1 }}>
          Analyze application security posture, attack surface, and credential hygiene.
        </Typography>

        <Grid container spacing={3}>
          {/* Security Analyzer */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, border: '2px solid', borderColor: 'secondary.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SecurityIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ color: 'secondary.main' }}>Security Analyzer</Typography>
                  <Chip label="Enhanced" size="small" color="secondary" sx={{ ml: 1, fontSize: '0.7rem' }} />
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Comprehensive security analysis for your applications including permissions, configurations, and security posture assessment.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Analyze all application permissions and identify risks" /></ListItem>
                  <ListItem><ListItemText primary="• Perform security configuration checks and compliance validation" /></ListItem>
                  <ListItem><ListItemText primary="• Calculate security scores and risk assessments" /></ListItem>
                  <ListItem><ListItemText primary="• Generate actionable security recommendations" /></ListItem>
                  <ListItem><ListItemText primary="• Export detailed security reports for auditing" /></ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Permission Analysis" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Security Checks" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small" label="Risk Assessment" sx={{ bgcolor: '#fce4ec', color: '#c2185b' }} />
                  <Chip size="small" label="Compliance" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Attack Surface Analyzer */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, border: '2px solid', borderColor: 'warning.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <GpsFixedIcon sx={{ color: 'warning.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: 'warning.main' }}>Attack Surface Analyzer</Typography>
                  <Chip label="New" size="small" color="warning" sx={{ ml: 1, fontSize: '0.7rem' }} />
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Advanced attack surface analysis from an adversary's perspective. Identify potential attack vectors, vulnerabilities, and exposure points.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Map authentication flow attack vectors and entry points" /></ListItem>
                  <ListItem><ListItemText primary="• Detect redirect URI vulnerabilities and open redirect risks" /></ListItem>
                  <ListItem><ListItemText primary="• Analyze token handling and session management flaws" /></ListItem>
                  <ListItem><ListItemText primary="• Identify API exposure and privilege escalation paths" /></ListItem>
                  <ListItem><ListItemText primary="• Assess cross-tenant access and tenant isolation risks" /></ListItem>
                  <ListItem><ListItemText primary="• Generate attack path visualizations and impact assessments" /></ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Attack Vectors" sx={{ bgcolor: '#ffebee', color: '#c62828' }} />
                  <Chip size="small" label="Vulnerability Scan" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                  <Chip size="small" label="Exposure Analysis" sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }} />
                  <Chip size="small" label="Risk Mapping" sx={{ bgcolor: '#fce4ec', color: '#c2185b' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Secrets Expiry Monitor */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Secrets Expiry Monitor</Typography>
                    <FeatureBadge label="New" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Inventory application and service principal credentials and track upcoming expirations. Filter, group by app, and export for audit.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Scan password and key credentials; include Service Principals optionally" /></ListItem>
                  <ListItem><ListItemText primary="• Risk buckets (<=90/30/7 days) and expired status; search by app or displayName" /></ListItem>
                  <ListItem><ListItemText primary="• CSV export of filtered results" /></ListItem>
                </List>
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Expiry tracking" sx={{ bgcolor: '#fff8e1', color: '#ff8f00' }} />
                  <Chip size="small" label="CSV export" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Security best practices (consolidated) */}
        <Card sx={{ mt: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Security Analysis Best Practices</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText primary="Regular Security Reviews" secondary="Perform security analysis after any application configuration changes" />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={4}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText primary="Follow Remediation Guidance" secondary="Implement recommended security mitigations promptly" />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={4}>
                <List dense>
                  <ListItem>
                    <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText primary="Monitor Security Scores" secondary="Track security improvements over time and maintain high scores" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* ══════ CATEGORY 6 — Application Management (2 tools) ══════ */}
      <Box ref={(el: HTMLDivElement | null) => { categoryRefs.current['management'] = el; }} sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 1.5, py: 1, borderRadius: 1, bgcolor: '#e0e7ff', borderLeft: '4px solid #4c1d95' }}>
          <BuildIcon sx={{ mr: 1, color: '#312e81' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#312e81', flex: 1 }}>Application Management</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 1 }}>
          Edit manifests, manage claims mapping policies, and configure application settings.
        </Typography>

        <Grid container spacing={3}>
          {/* Manifest Editor */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CodeIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Manifest Editor</Typography>
                    <FeatureBadge label="AppConfig² Only" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Edit application manifests directly to fine-tune application configurations (AppConfig² only).
                  In AppTesting, manifests are available in read-only viewer mode.
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• View and edit the full JSON manifest" /></ListItem>
                  <ListItem><ListItemText primary="• Make advanced configuration changes" /></ListItem>
                  <ListItem><ListItemText primary="• Apply manifest changes directly to applications" /></ListItem>
                  <ListItem><ListItemText primary="• Validate manifest syntax before applying" /></ListItem>
                </List>
                <Box sx={{ mt: 2, bgcolor: '#f0f7ff', p: 2, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Note:</strong> Direct manifest editing gives you full control but requires careful attention.
                    Use the structured interface for common tasks when possible.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Claims Mapping Policy Tool */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ContentCopyIcon color="primary" sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Typography variant="h6">Claims Mapping Policy Tool</Typography>
                    <FeatureBadge label="AppConfig² Only" />
                  </Box>
                </Box>
                <Typography paragraph variant="body2" color="text.secondary">
                  Create and manage claims mapping policies for your applications. This tool helps you:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="• Create new policies with custom claims" /></ListItem>
                  <ListItem><ListItemText primary="• Assign policies to specific applications" /></ListItem>
                  <ListItem><ListItemText primary="• Review and edit existing policies" /></ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="Custom claims" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                  <Chip size="small" label="Assign to apps" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                  <Chip size="small" label="Review policies" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ToolsTab;
